import {
  Box,
  Typography,
  Paper,
  Divider,
  FormHelperText,
  IconButton,
  TextField,
  Autocomplete,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Controller,
  useFieldArray,
  useWatch,
  type Control,
} from "react-hook-form";
import { NodoCreateOrUpdateDto, useDownloadFile } from "@/api";
import Button from "@/elements/Button";
import { Add, Delete, Close } from "@mui/icons-material";
import FileField from "../FileField";
import { NodoPicker } from "../NodoPicker";

export type NodoDialogImmagineDinamicaProps = {
  control: Control<NodoCreateOrUpdateDto>;
};

export default function NodoDialogImmagineDinamica({
  control,
}: NodoDialogImmagineDinamicaProps) {
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "immagineDinamicaElementi",
  });

  const immagineDinamicaSvgId = useWatch({
    control,
    name: "immagineDinamicaSvgId",
  });

  const { data: svgData, isLoading: isLoadingSvg } = useDownloadFile(
    immagineDinamicaSvgId ?? 0,
    {
      query: {
        enabled: !!immagineDinamicaSvgId,
      },
      axios: { responseType: "blob" },
    }
  );

  const [svgContent, setSvgContent] = useState("");

  useEffect(() => {
    if (!immagineDinamicaSvgId) {
      setSvgContent("");
      return;
    }

    if (!svgData?.data) {
      setSvgContent("");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSvgContent(reader.result as string);
    };
    reader.readAsText(svgData.data as Blob);
  }, [immagineDinamicaSvgId, svgData]);

  const svgElementIds = useMemo(() => {
    if (!svgContent) {
      return [];
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, "image/svg+xml");
      const elements = Array.from(
        doc.querySelectorAll(
          "rect[id], circle[id], ellipse[id], path[id], polygon[id], polyline[id]"
        )
      );
      const ids = elements.map((element) => element.id).filter((id) => !!id);
      return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
    } catch (error) {
      return [];
    }
  }, [svgContent]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <Box sx={{ minWidth: 200, flex: 1 }}>
          <Controller
            name="immagineDinamicaSvgId"
            control={control}
            render={({ field, fieldState }) => (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {!field.value && (
                  <FileField
                    accept="image/svg+xml"
                    onChange={(f) => {
                      field.onChange(f?.id ?? null);
                    }}
                  />
                )}
                {field.value && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      minHeight: 160,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      bgcolor: "background.paper",
                      position: "relative",
                      "&:hover .delete-icon": { opacity: 1 },
                    }}
                  >
                    <IconButton
                      className="delete-icon"
                      size="small"
                      color="error"
                      onClick={() => field.onChange(null)}
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: -12,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "error.main",
                        opacity: 0,
                        transition: "opacity 0.2s",
                        "&:hover": {
                          bgcolor: "error.lighter",
                        },
                        zIndex: 1,
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                    {svgContent ? (
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          "& svg": {
                            maxWidth: "100%",
                            height: "auto",
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: svgContent }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isLoadingSvg
                          ? t("azioni.caricamento", "Caricamento SVG...")
                          : t(
                              "nodoDialog.messaggi.svgPreviewAssente",
                              "Anteprima SVG non disponibile"
                            )}
                      </Typography>
                    )}
                  </Paper>
                )}
                {fieldState.error && (
                  <FormHelperText error>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        </Box>
      </Box>

      <Divider />

      {/* Mappatura */}
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            {t("nodoDialog.campi.mappaturaSvg")}
          </Typography>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() =>
              append({
                svgElementId: "",
                nodoDispositivoId: 0,
              })
            }
            startIcon={<Add />}
          >
            {t("azioni.aggiungi")}
          </Button>
        </Box>

        {fields.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: "action.hover",
              borderStyle: "dashed",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t(
                "nodoDialog.messaggi.nessunaMappatura",
                "Nessuna mappatura configurata"
              )}
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              p: 1.5,
              mb: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            {fields.map((fieldItem, index) => (
              <Box
                key={fieldItem.id}
                display="flex"
                flexWrap="wrap"
                gap={2}
                alignItems="flex-start"
                sx={{
                  pb: index === fields.length - 1 ? 0 : 1.5,
                  borderBottom:
                    index === fields.length - 1 ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Controller
                    name={`immagineDinamicaElementi.${index}.svgElementId`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        options={svgElementIds}
                        getOptionDisabled={(option) =>
                          fields.some(
                            (f, i) => i !== index && f.svgElementId === option
                          )
                        }
                        value={
                          svgElementIds.includes(field.value)
                            ? field.value
                            : null
                        }
                        onChange={(_, value) => field.onChange(value ?? "")}
                        size="small"
                        disabled={svgElementIds.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t("nodoDialog.campi.svgElementId")}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ flex: 2, minWidth: 250 }}>
                  <Controller
                    name={`immagineDinamicaElementi.${index}.nodoDispositivoId`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <Box>
                        <NodoPicker
                          grow="vertically"
                          value={field.value ? { id: field.value } : null}
                          onChange={(nodo) => field.onChange(nodo?.id ?? 0)}
                          filterSelection={(n) =>
                            n.tipo === "Azienda" ||
                            n.tipo === "Contenitore" ||
                            n.tipo === "Dispositivo_Macchinario" ||
                            n.tipo === "Dispositivo_Sensore"
                          }
                        />
                        {fieldState.error && (
                          <FormHelperText error>
                            {fieldState.error.message}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                  />
                </Box>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => remove(index)}
                  startIcon={<Delete />}
                >
                  {t("azioni.elimina")}
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
